import { callable } from './callable.js'
import { uploadWorkspaceFiles } from './storageHubService.js'
import { generateEduRequestId } from './eduAnnouncementService.js'

export { generateEduRequestId }

export interface OfficeHoursWindow {
  day: number // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  start: string // HH:MM
  end: string // HH:MM
}

export interface SetEduMessagingSettingsInput {
  workspaceId: string
  enabled: boolean
}

export interface SetEduOfficeHoursInput {
  workspaceId: string
  windows: OfficeHoursWindow[]
}

export interface GetEduOfficeHoursInput {
  workspaceId: string
  staffUid?: string
}

export interface ListEduMessageableTeachersInput {
  workspaceId: string
  learnerId: string
}

export interface MessageableTeacher {
  uid: string
  displayName: string
  role: string
  officeHours: OfficeHoursWindow[]
  isInsideHours: boolean
  isInsideOfficeHours?: boolean
  nextWindowStart: string | null
}

export interface OpenEduThreadInput {
  workspaceId: string
  learnerId: string
  staffUid: string
}

export interface EduThread {
  id: string
  workspaceId: string
  learnerId: string
  staffUid: string
  guardianUids: string[]
  selfUid: string | null
  lastMessageAt: unknown | null
  unreadCountStaff: number
  unreadCountFamily: number
  createdAt: unknown
}

export interface SendEduThreadMessageInput {
  workspaceId: string
  threadId: string
  text: string
  fileIds?: string[]
  requestId: string
}

export interface EduThreadMessageAttachment {
  id?: string
  fileId?: string
  name: string
  size?: number
  contentType?: string
  downloadUrl?: string
}

export interface EduThreadMessage {
  id: string
  workspaceId: string
  threadId: string
  senderUid: string
  senderRole: 'STAFF' | 'FAMILY'
  text: string
  attachments: EduThreadMessageAttachment[]
  requestId: string
  createdAt: unknown
  editedAt: unknown | null
  deletedAt: unknown | null
  visible?: boolean
}

export type EduThreadItem = EduThread
export type EduMessageItem = EduThreadMessage

export interface EditEduThreadMessageInput {
  workspaceId: string
  messageId: string
  text: string
}

export interface DeleteEduThreadMessageInput {
  workspaceId: string
  messageId: string
}

export interface ListEduThreadsInput {
  workspaceId: string
  learnerId?: string
  all?: boolean
  cursor?: string
}

export interface ListEduThreadMessagesInput {
  workspaceId: string
  threadId: string
  cursor?: string
}

export interface MarkEduThreadReadInput {
  workspaceId: string
  threadId: string
}

export async function setEduMessagingSettings(
  input: SetEduMessagingSettingsInput,
): Promise<{ success: boolean; enabled: boolean }> {
  return callable('setEduMessagingSettings', input)
}

export async function setEduOfficeHours(
  input: SetEduOfficeHoursInput,
): Promise<{ success: boolean; windows: OfficeHoursWindow[] }> {
  return callable('setEduOfficeHours', input)
}

export async function getEduOfficeHours(
  input: GetEduOfficeHoursInput,
): Promise<{
  windows: OfficeHoursWindow[]
  isInsideHours: boolean
  isInsideOfficeHours?: boolean
  nextWindowStart: string | null
  timezone: string
}> {
  return callable('getEduOfficeHours', input)
}

export async function listEduMessageableTeachers(
  input: ListEduMessageableTeachersInput,
): Promise<{ teachers: MessageableTeacher[]; messagingEnabled: boolean }> {
  return callable('listEduMessageableTeachers', input)
}

export async function openEduThread(
  input: OpenEduThreadInput,
): Promise<{ thread: EduThread; created: boolean }> {
  return callable('openEduThread', input)
}

export async function sendEduThreadMessage(
  input: SendEduThreadMessageInput,
): Promise<{ message: EduThreadMessage; heldUntil?: string | null }> {
  return callable('sendEduThreadMessage', input)
}

export async function editEduThreadMessage(
  input: EditEduThreadMessageInput,
): Promise<{ success: boolean; message: EduThreadMessage }> {
  return callable('editEduThreadMessage', input)
}

export async function deleteEduThreadMessage(
  input: DeleteEduThreadMessageInput,
): Promise<{ success: boolean }> {
  return callable('deleteEduThreadMessage', input)
}

export async function listEduThreads(
  input: ListEduThreadsInput,
): Promise<{ threads: EduThread[]; nextCursor: string | null; messagingEnabled: boolean }> {
  return callable('listEduThreads', input)
}

export async function listEduThreadMessages(
  input: ListEduThreadMessagesInput,
): Promise<{ messages: EduThreadMessage[]; nextCursor: string | null }> {
  return callable('listEduThreadMessages', input)
}

export async function markEduThreadRead(
  input: MarkEduThreadReadInput,
): Promise<{ success: boolean; unreadCountStaff: number; unreadCountFamily: number }> {
  return callable('markEduThreadRead', input)
}

export interface UploadThreadMessageFilesInput {
  workspaceId: string
  requestId: string
  files: File[]
  onProgress?: (completed: number, total: number, fileName: string) => void
}

export async function uploadThreadMessageFiles(
  input: UploadThreadMessageFilesInput,
): Promise<string[]> {
  if (input.files.length === 0) return []
  const results = await uploadWorkspaceFiles({
    workspaceId: input.workspaceId,
    files: input.files,
    visibility: 'PRIVATE',
    artifactType: 'EDU_MESSAGE',
    artifactId: input.requestId,
    onProgress: input.onProgress,
  })
  const fileIds: string[] = []
  for (const res of results) {
    if (!res.fileId) {
      throw new Error('Upload completed without returning a file ID.')
    }
    fileIds.push(res.fileId)
  }
  return fileIds
}
